import React, { useState } from 'react';
import {
  Card,
  List,
  Tag,
  Typography,
  Button,
  Space,
  Empty,
  Badge,
  Select,
  Input,
  message,
} from 'antd';
import {
  BellOutlined,
  MessageOutlined,
  ShoppingOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  SettingOutlined,
  GiftOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;

const Notifications = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const {
    data: notificationData,
    isLoading,
    refetch,
  } = useQuery(
    'provider-notifications',
    async () => {
      const response = await api.get('/provider/notifications');
      return response.data;
    },
    {
      refetchInterval: 30000,
    },
  );

  const markAsReadMutation = useMutation(
    async (id) => {
      const response = await api.post(`/provider/notifications/${id}/mark-read`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Marked as read');
        queryClient.invalidateQueries('provider-notifications');
      },
    },
  );

  const markAllAsReadMutation = useMutation(
    async () => {
      const response = await api.post('/provider/notifications/read-all');
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('All notifications marked as read');
        queryClient.invalidateQueries('provider-notifications');
      },
    },
  );

  const deleteNotificationMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/provider/notifications/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Notification deleted');
        queryClient.invalidateQueries('provider-notifications');
      },
      onError: () => {
        message.error('Failed to delete notification');
      },
    },
  );

  const notifications = notificationData?.data || [];
  const unreadCount = notificationData?.unread_count || 0;

  const filteredNotifications = notifications.filter((n) => {
    const matchesSearch =
      !searchTerm ||
      n.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      n.message?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !typeFilter || n.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const getIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageOutlined style={{ color: '#1890ff' }} />;
      case 'order':
        return <ShoppingOutlined style={{ color: '#52c41a' }} />;
      case 'payment':
        return <DollarOutlined style={{ color: '#faad14' }} />;
      case 'promotion':
        return <GiftOutlined style={{ color: '#722ed1' }} />;
      case 'system':
        return <SettingOutlined style={{ color: '#666' }} />;
      default:
        return <BellOutlined />;
    }
  };

  const getTagColor = (type) => {
    switch (type) {
      case 'message':
        return 'blue';
      case 'order':
        return 'green';
      case 'payment':
        return 'gold';
      case 'promotion':
        return 'purple';
      case 'system':
        return 'default';
      default:
        return 'default';
    }
  };

  const getNotificationLink = (item) => {
    let data = null;
    try {
      data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
    } catch (e) {}

    if (item.type === 'message' && data?.sender_id) {
      return `/chat?conversation=${data.sender_id}`;
    } else if (item.type === 'order' && data?.order_id) {
      return `/orders?booking=${data.order_id}`;
    } else if (item.type === 'promotion') {
      return '/products';
    }
    return null;
  };

  const handleNotificationClick = async (item) => {
    const link = getNotificationLink(item);

    if (link) {
      window.location.href = link;
    } else {
      if (!item.is_read) {
        markAsReadMutation.mutate(item.id);
      }
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            <BellOutlined style={{ marginRight: '8px' }} />
            Notifications
          </Title>
          {unreadCount > 0 && <Badge count={unreadCount} style={{ backgroundColor: '#ff4d4f' }} />}
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => markAllAsReadMutation.mutate()}
              loading={markAllAsReadMutation.isLoading}
            >
              Mark all read
            </Button>
          )}
        </Space>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Search
            placeholder="Search notifications..."
            allowClear
            style={{ width: 250 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
          />
          <Select
            placeholder="Filter by type"
            allowClear
            style={{ width: 150 }}
            onChange={setTypeFilter}
          >
            <Select.Option value="message">Message</Select.Option>
            <Select.Option value="order">Order</Select.Option>
            <Select.Option value="payment">Payment</Select.Option>
            <Select.Option value="promotion">Promotion</Select.Option>
            <Select.Option value="system">System</Select.Option>
          </Select>
          <Text type="secondary">
            {filteredNotifications.length} of {notifications.length} notifications
          </Text>
        </Space>
      </Card>

      {/* Notifications List */}
      <Card>
        {filteredNotifications.length > 0 ? (
          <List
            loading={isLoading}
            dataSource={filteredNotifications}
            renderItem={(item) => (
              <List.Item
                style={{
                  background: item.is_read ? 'transparent' : '#f6ffed',
                  padding: '16px',
                  marginBottom: 8,
                  borderRadius: 8,
                  border: item.is_read ? '1px solid #f0f0f0' : '1px solid #b7eb8f',
                  cursor: 'pointer',
                }}
                onClick={() => handleNotificationClick(item)}
                actions={[
                  !item.is_read && (
                    <Button
                      type="link"
                      size="small"
                      icon={<CheckCircleOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsReadMutation.mutate(item.id);
                      }}
                      loading={markAsReadMutation.isLoading}
                    >
                      Mark read
                    </Button>
                  ),
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotificationMutation.mutate(item.id);
                    }}
                    loading={deleteNotificationMutation.isLoading}
                  >
                    Delete
                  </Button>,
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: item.is_read ? '#f0f0f0' : '#e6f7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                      }}
                    >
                      {getIcon(item.type)}
                    </div>
                  }
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text strong={!item.is_read} style={{ fontSize: 15 }}>
                        {item.title}
                      </Text>
                      <Tag color={getTagColor(item.type)} style={{ marginLeft: 4 }}>
                        {item.type}
                      </Tag>
                      {item.is_read && <Tag color="default">Read</Tag>}
                    </div>
                  }
                  description={
                    <div>
                      <div style={{ marginBottom: 4, fontSize: 14, color: '#333' }}>
                        {item.message}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 12,
                          color: '#888',
                        }}
                      >
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        {dayjs(item.created_at).format('MMM D, YYYY HH:mm')}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty
            description={
              searchTerm || typeFilter ? 'No matching notifications' : 'No notifications'
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {(searchTerm || typeFilter) && (
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setTypeFilter('');
                }}
              >
                Clear filters
              </Button>
            )}
          </Empty>
        )}
      </Card>

      {/* Stats */}
      <Card style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff4d4f' }}>{unreadCount}</div>
            <div style={{ color: '#888' }}>Unread</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
              {notifications.filter((n) => n.is_read).length}
            </div>
            <div style={{ color: '#888' }}>Read</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
              {notifications.length}
            </div>
            <div style={{ color: '#888' }}>Total</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Notifications;
